const SUPER_ADMIN_ROLE = 'super_admin';

export function userRoles(): string[] {
    return window.__V2__?.roles ?? [];
}

export function isSuperAdmin(): boolean {
    return userRoles().includes(SUPER_ADMIN_ROLE);
}

/** Vendas → Inadimplências: super_admin, CFO e Contas a Receber (View:DefaultedInstallments). */
export function canViewDefaulted(): boolean {
    return window.__V2__?.can_view_defaulted === true || isSuperAdmin();
}

export function canViewGluteleader(): boolean {
    return window.__V2__?.can_view_gluteleader === true || isSuperAdmin();
}

/** Inspetor de campos Ploomes (deal / quotes / OtherProperties). */
export function canInspectPloomesFields(): boolean {
    return window.__V2__?.can_inspect_ploomes_fields === true || isSuperAdmin();
}

/** Sincronizar deal com Ploomes (admin / super_admin ou SyncPloomes:PloomesDeal). */
export function canSyncPloomesDeal(): boolean {
    if (isSuperAdmin() || hasRole('admin')) {
        return true;
    }

    return window.__V2__?.can_sync_ploomes_deal === true;
}

/** Extrair relatório Excel da lista de propostas Ploomes. */
export function canExportPloomesDeal(): boolean {
    if (isSuperAdmin() || hasRole('admin')) {
        return true;
    }

    return window.__V2__?.can_export_ploomes_deal === true;
}

/** Verifica se o usuário possui um role específico. super_admin sempre retorna true. */
export function hasRole(role: string): boolean {
    if (isSuperAdmin()) return true;
    return userRoles().includes(role);
}

/** Verifica se o usuário possui ao menos um dos roles listados. super_admin sempre retorna true. */
export function hasAnyRole(roles: string[]): boolean {
    if (isSuperAdmin()) return true;
    const current = userRoles();
    return roles.some((r) => current.includes(r));
}

// ─── Grupos de roles ───────────────────────────────────────────

/** Administradores do sistema */
export const ADMIN_ROLES = ['super_admin', 'admin'] as const;

/** Operacional de COMEX + administração */
export const COMEX_ROLES = ['super_admin', 'admin', 'comex'] as const;

/** Financeiro + administração */
export const FINANCIAL_ROLES = ['super_admin', 'admin', 'financial', 'financeiro'] as const;

/** Logística + administração */
export const LOGISTICS_ROLES = ['super_admin', 'admin', 'comex', 'logistics', 'logistica'] as const;

/** Todos os roles operacionais — podem visualizar embarques */
export const ALL_OPERATIONAL_ROLES = [
    'super_admin', 'admin',
    'comex', 'financial', 'financeiro',
    'logistics', 'logistica',
    'cargo_agent', 'customs_broker', 'transport', 'cx',
] as const;

/** Roles com acesso a CRM operacional (COMEX dashboard, equipment, anexos) */
export const CRM_ROLES = ['super_admin', 'admin', 'comex', 'financial', 'financeiro'] as const;

/** Roles com acesso a Propostas Ganhas / deals Ploomes (vendas) */
export const SALES_ROLES = [
    ...CRM_ROLES,
    'salesperson',
    'sales_manager',
    'commercial_director',
    'hr',
    'financial_director',
    'accounts_receivable',
] as const;

/** Roles com acesso a clientes e pedidos */
export const CX_ROLES = ['super_admin', 'admin', 'comex', 'financial', 'financeiro', 'cx'] as const;

/** Roles com acesso ao módulo de comissionamento — todos os perfis comerciais */
export const COMMISSION_ROLES = [
    'super_admin', 'admin',
    'salesperson', 'sales_manager', 'commercial_director',
    'hr', 'financial_director',
] as const;

// ─── Helpers de comissionamento ───────────────────────────────────

/**
 * Retorna o role de comissão do usuário atual (o mais privilegiado na hierarquia).
 * null = usuário sem nenhum role de comissionamento.
 */
export function commissionRole(): string | null {
    return (window.__V2__ as Record<string, unknown>)?.commission_role as string | null ?? null;
}

/** Gate do módulo de comissionamento (ViewAny:CommissionModule). */
export function canAccessCommissionModule(): boolean {
    return window.__V2__?.can_access_commission_module === true || isSuperAdmin();
}

/** Verdadeiro se o usuário tem qualquer acesso ao módulo de comissionamento. */
export function canAccessCommission(): boolean {
    return canAccessCommissionModule() && commissionRole() !== null;
}

/** Consulta de estoque (ViewAny:StockConsult). */
export function canViewStockConsult(): boolean {
    return window.__V2__?.can_view_stock_consult === true || isSuperAdmin();
}

/** Lista/detalhe de produtos (ViewAny:Product). */
export function canViewProducts(): boolean {
    return window.__V2__?.can_view_products === true || isSuperAdmin();
}

/** Verdadeiro apenas para vendedores individuais (sem papel gerencial). */
export function isVendedor(): boolean {
    return commissionRole() === 'salesperson';
}

/** Verdadeiro para gestores/gerentes de equipe comercial. */
export function isGestorVendas(): boolean {
    return commissionRole() === 'sales_manager';
}

/** Verdadeiro para o diretor comercial (vê tudo de vendas e comissão). */
export function isDiretorComercial(): boolean {
    const role = commissionRole();
    return role === 'commercial_director' || role === 'super_admin' || role === 'admin';
}

/** Verdadeiro para o diretor financeiro/CFO (foco em aprovação e cashflow). */
export function isDiretorFinanceiro(): boolean {
    return commissionRole() === 'financial_director';
}

/** Verdadeiro para o perfil de RH (aprovação de NFs). */
export function isRh(): boolean {
    return commissionRole() === 'hr';
}

/**
 * Adicional pessoal do vendedor (ex.: +1,5% Rafael).
 * Visível para super_admin, admin, financeiro, RH e o próprio beneficiário.
 * Diretor comercial NÃO vê — fonte: boot `__V2__.can_view_seller_bonus`.
 */
export function canViewSellerBonus(): boolean {
    return window.__V2__?.can_view_seller_bonus === true || isSuperAdmin() || hasRole('admin');
}

/** Verdadeiro se o usuário pode fazer ações financeiras (aprovar NFs, marcar pago). */
export function canApproveCommission(): boolean {
    if (isSuperAdmin() || hasRole('admin')) {
        return true;
    }

    const role = commissionRole();
    return ['hr', 'financial_director', 'commercial_director'].includes(role ?? '');
}

/** IDs dos usuários do time gerenciado (preenchido apenas para gestor_vendas). */
export function commissionManagedUserIds(): number[] {
    return ((window.__V2__ as Record<string, unknown>)?.commission_managed_user_ids as number[] | null) ?? [];
}
